/**
 * Feature flag constants used across the app.
 */
export const FEATURE_FLAGS = {
  WORKSPACES: "workspaces",
  DASHBOARD_P50: "dashboard-p50",
  COVAL_ONLY: "coval-only",
  CUSTOM_WRAPPER_OBSERVATIONS: "chime-wrapper-observations",
  API_SIMULATION_WRAPPER: "api-simulation-wrapper",
  UPLOAD_SESSION_IDS_TO_SIMULATIONS: "upload-session-ids-to-simulations",
  BENCHMARKS: "benchmarks",
  AGENT_MUTATIONS: "agent-mutations",
  IMAGE_TEST_SET: "image-test-set",
  RUBRIC_DECISION_TREE: "rubric-decision-tree",
  AGENT_TEST_CONNECTION: "agent-test-connection",
  EVAL_GRAPHS: "eval-graphs",
  SQL_FLOAT_METRIC: "sql-float-metric",
  AGENT_JUDGE_METRIC: "agent-judge-metric",
  CONVERSATION_TRIAGE: "conversation-triage",
  TRANSCRIPT_TOOL_CALL_NAMES: "transcript-tool-call-names",
  PLAYBACK_MARKERS: "playback-markers",
  PROJECT_INSIGHTS_TILE_GRID: "insights-tile-grid",
  HUMAN_REVIEW_COMPARISON_INSIGHTS: "human-review-comparison-insights",
  HUMAN_REVIEW_ROLE_MODES: "human-review-role-modes",
  SIMULATION_ROOMS: "simulation-rooms",
  HUMAN_REVIEW_ENFORCED_COLLABORATION: "human-review-enforced-collaboration",
  IMPROVEMENT_LOOP_NAV: "improvement-loop-nav",
  OPENAI_GPT_LIVE_ALPHA: "openai-gpt-live-alpha",
  SOFIA_NOTEBOOKS: "sofia-notebooks",
  SOFIA_SLACK: "sofia-slack",
  GENESYS_CONVERSATION_MONITORING: "genesys-conversation-monitoring",
  PERSONA_CUSTOM_VOICE_DESIGN: "persona-custom-voice-design",
  METRIC_AUDIT_V0: "metric-audit-v0",
} as const

/**
 * Local dev overrides for feature flags.
 * These bypass PostHog in development, so you don't need PostHog enabled.
 * Only flags explicitly set here will be overridden - others fall through to PostHog.
 */
export const LOCAL_DEV_FLAGS: Partial<Record<string, boolean>> = {
  // Internal dev-only tooling is gated by the coval-only flag in production
  // (PostHog targets Coval users); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.COVAL_ONLY]: true,
  [FEATURE_FLAGS.API_SIMULATION_WRAPPER]: true,
  [FEATURE_FLAGS.BENCHMARKS]: true,
  [FEATURE_FLAGS.IMAGE_TEST_SET]: true,
  [FEATURE_FLAGS.RUBRIC_DECISION_TREE]: true,
  [FEATURE_FLAGS.AGENT_TEST_CONNECTION]: true,
  [FEATURE_FLAGS.EVAL_GRAPHS]: true,
  [FEATURE_FLAGS.SQL_FLOAT_METRIC]: true,
  // Agent Judge is rolled out per organization through PostHog; local development
  // keeps the authoring flow available without a PostHog project.
  [FEATURE_FLAGS.AGENT_JUDGE_METRIC]: true,
  [FEATURE_FLAGS.CONVERSATION_TRIAGE]: true,
  // Tool call names inline in transcripts ship dark in production (PostHog
  // per-org rollout); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.TRANSCRIPT_TOOL_CALL_NAMES]: true,
  // Playback/speech-activity markers ship dark in production (PostHog
  // per-org rollout); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.PLAYBACK_MARKERS]: true,
  // Workspaces is gated by a PostHog per-org flag in production; local dev
  // keeps it on so developers can test without PostHog configured.
  [FEATURE_FLAGS.WORKSPACES]: true,
  // The Project Insights tile grid ships dark in production (PostHog per-org
  // rollout); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.PROJECT_INSIGHTS_TILE_GRID]: true,
  // Human Review comparison insights ship dark in production (PostHog per-org
  // rollout); local dev keeps the aggregate-backed widgets on for validation.
  [FEATURE_FLAGS.HUMAN_REVIEW_COMPARISON_INSIGHTS]: true,
  // The split Human Review landing pages ship dark in production while the
  // assignee and manager workflows are validated together.
  [FEATURE_FLAGS.HUMAN_REVIEW_ROLE_MODES]: true,
  // Simulation rooms ship dark in production (PostHog per-org rollout) while
  // LiveKit is in vendor review; local dev keeps them on without PostHog.
  [FEATURE_FLAGS.SIMULATION_ROOMS]: true,
  // The improvement-loop sidebar is targeted at Coval users in PostHog while
  // it settles; local dev keeps it on without PostHog. It carries its own flag
  // rather than riding COVAL_ONLY so it can widen past Coval on its own.
  [FEATURE_FLAGS.IMPROVEMENT_LOOP_NAV]: true,
  // The confidential OpenAI alpha is targeted to approved organizations in
  // PostHog. Local development keeps the selector available for validation.
  [FEATURE_FLAGS.OPENAI_GPT_LIVE_ALPHA]: true,
  // Sofia notebooks are initially targeted at Coval users in PostHog; local
  // development keeps them on without PostHog.
  [FEATURE_FLAGS.SOFIA_NOTEBOOKS]: true,
  // Sofia-in-Slack remains dark in production until an explicit PostHog
  // audience is configured. Local development keeps the gated UI available
  // for cal-demo-dev validation.
  [FEATURE_FLAGS.SOFIA_SLACK]: true,
  // Genesys conversation monitoring remains dark in production while the
  // connector is validated with the Genesys partner lab.
  [FEATURE_FLAGS.GENESYS_CONVERSATION_MONITORING]: true,
  [FEATURE_FLAGS.PERSONA_CUSTOM_VOICE_DESIGN]: true,
  [FEATURE_FLAGS.METRIC_AUDIT_V0]: true,
}
